<template>
  <div class="flex items-end w-full">
    <div
      id="svg-container"
      class="w-full sm:max-w-[400px] md:max-w-[500px] lg:max-w-[650px] aspect-square bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.15),0_10px_20px_-5px_rgba(0,0,0,0.1),0_0_0_1px_rgba(0,0,0,0.05)] border-2 border-[var(--p-surface-100)] transition-all duration-300 relative overflow-visible hover:shadow-[0_30px_60px_-12px_rgba(0,0,0,0.2),0_15px_30px_-5px_rgba(0,0,0,0.15),0_0_0_1px_rgba(0,0,0,0.05)] hover:-translate-y-0.5"
    >
      <svg
        ref="segCirc"
        id="segCirc"
        viewBox="0 0 500 500"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        class="w-full h-full"
        preserveAspectRatio="xMidYMid meet"
      ></svg>
    </div>

    <Transition name="popover-fade">
      <div
        v-if="showPopover && selectedMonth"
        class="month-progress-popover relative min-w-[320px] max-w-[360px]"
        :style="popoverStyle"
      >
        <div
          class="popover-arrow absolute w-0 h-0 border-8 border-transparent border-b-white -top-4 left-6 drop-shadow-[0_-2px_4px_rgba(0,0,0,0.1)]"
        ></div>
        <div
          class="popover-content bg-white rounded-2xl shadow-[0_20px_25px_-5px_rgba(0,0,0,0.1),0_10px_10px_-5px_rgba(0,0,0,0.04),0_0_0_1px_rgba(0,0,0,0.05)] overflow-hidden backdrop-blur-[10px]"
        >
          <div
            class="popover-header flex items-center gap-3 px-5 pt-5 pb-4 bg-gradient-to-br from-[var(--p-primary-50)] to-[var(--p-primary-100)] border-b border-[var(--p-surface-200)]"
          >
            <div
              class="popover-header-icon w-10 h-10 rounded-xl bg-gradient-to-br from-[var(--p-primary-500)] to-[var(--p-primary-600)] flex items-center justify-center text-white text-lg flex-shrink-0 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1)]"
            >
              <i class="pi pi-calendar text-white"></i>
            </div>
            <div class="popover-header-text flex-1 min-w-0">
              <h4
                class="popover-title m-0 text-lg font-bold text-[var(--p-anthracite-500)] leading-tight tracking-[-0.01em]"
              >
                {{ selectedMonth.name }}
              </h4>
              <p
                class="popover-subtitle mt-0.5 mb-0 text-xs font-medium text-[var(--p-anthracite-500)] uppercase tracking-[0.05em]"
              >
                Year-to-Date Progress
              </p>
            </div>
          </div>
          <div class="popover-body p-5 bg-white">
            <div class="progress-stats flex flex-col gap-4 mb-5">
              <div
                class="stat-item flex items-center gap-3 p-3 rounded-xl bg-[var(--p-surface-50)] transition-all duration-200"
              >
                <div
                  class="stat-icon expected w-10 h-10 rounded-[10px] flex items-center justify-center text-white text-lg flex-shrink-0 bg-gradient-to-br from-[var(--p-primary-500)] to-[var(--p-primary-600)]"
                >
                  <i class="pi pi-flag text-white"></i>
                </div>
                <div class="stat-content flex-1 flex flex-col gap-1 min-w-0">
                  <span
                    class="stat-label text-xs font-semibold text-[var(--p-anthracite-500)] uppercase tracking-[0.05em]"
                    >Expected</span
                  >
                  <span
                    class="stat-value expected-value text-base font-bold text-[var(--p-primary-700)] leading-tight"
                    >{{ formatCurrency(selectedMonth.expected) }}</span
                  >
                </div>
              </div>
              <div
                class="stat-item flex items-center gap-3 p-3 rounded-xl bg-[var(--p-surface-50)] transition-all duration-200"
              >
                <div
                  class="stat-icon actual w-10 h-10 rounded-[10px] flex items-center justify-center text-white text-lg flex-shrink-0 bg-gradient-to-br from-[var(--p-mint-500)] to-[var(--p-mint-600)]"
                >
                  <i class="pi pi-chart-line text-white"></i>
                </div>
                <div class="stat-content flex-1 flex flex-col gap-1 min-w-0">
                  <span
                    class="stat-label text-xs font-semibold text-[var(--p-anthracite-500)] uppercase tracking-[0.05em]"
                    >Actual</span
                  >
                  <span
                    class="stat-value actual-value text-base font-bold text-[var(--p-mint-700)] leading-tight"
                    >{{ formatCurrency(selectedMonth.actual) }}</span
                  >
                </div>
              </div>
              <div
                class="stat-item highlight flex items-center gap-3 p-3.5 rounded-xl bg-gradient-to-br from-[var(--p-primary-50)] to-[var(--p-primary-100)] border-2 border-[var(--p-primary-200)] transition-all duration-200"
              >
                <div
                  class="stat-icon progress w-10 h-10 rounded-[10px] flex items-center justify-center text-white text-lg flex-shrink-0 bg-gradient-to-br from-[var(--p-tangerine-500)] to-[var(--p-tangerine-600)]"
                >
                  <i class="pi pi-percentage text-white"></i>
                </div>
                <div class="stat-content flex-1 flex flex-col gap-1 min-w-0">
                  <span
                    class="stat-label text-xs font-semibold text-[var(--p-anthracite-500)] uppercase tracking-[0.05em]"
                    >Progress</span
                  >
                  <span
                    class="stat-value progress-percentage text-xl font-extrabold text-[var(--p-tangerine-700)] leading-tight"
                    >{{ selectedMonth.percentage }}%</span
                  >
                </div>
              </div>
            </div>
            <div class="progress-bar-container mt-2">
              <div class="progress-bar-wrapper">
                <ProgressBar
                  :value="selectedMonth.percentage"
                  :showValue="false"
                  class="month-progress-bar h-2.5 rounded-md overflow-hidden"
                />
                <div
                  class="progress-bar-labels flex justify-between mt-1.5 text-[11px] font-semibold text-[var(--p-anthracite-500)] tracking-[0.02em]"
                >
                  <span class="progress-min">0%</span>
                  <span class="progress-max">100%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { useTemplateRef, onMounted, ref, computed, watch } from 'vue'
import type { MonthData } from '@/config/types/SegmentedCircle'

const svg = useTemplateRef('segCirc')
const totalSegments = 12

const showPopover = ref(false)
const selectedMonth = ref<MonthData | null>(null)
const mousePosition = ref({ x: 0, y: 0 })

const monthData = ref<MonthData[]>([])

const popoverStyle = computed(() => {
  if (!showPopover.value) return {}

  const popoverWidth = 320
  const popoverHeight = 240
  const offset = 15
  const padding = 20

  let left = mousePosition.value.x + offset
  let top = mousePosition.value.y - offset

  if (left + popoverWidth > window.innerWidth - padding) {
    left = mousePosition.value.x - popoverWidth - offset
  }

  if (left < padding) {
    left = padding
  }

  if (top + popoverHeight > window.innerHeight - padding) {
    top = mousePosition.value.y - popoverHeight - offset
  }

  if (top < padding) {
    top = padding
  }

  return {
    position: 'fixed' as const,
    top: `${top}px`,
    left: `${left}px`,
    zIndex: 1000,
    pointerEvents: 'none' as const,
  }
})

const props = defineProps({
  filledSegments: {
    type: Number,
    default: 0,
  },
  progressData: {
    type: Object,
    default: () => ({
      actualTurnover: 0,
      anticipatedVolume: 0,
      currentMonth: new Date().getMonth(),
    }),
  },
  isDataLoaded: {
    type: Boolean,
    default: false,
  },
})

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(value || 0)
}

const calculateMonthData = () => {
  const { actualTurnover, anticipatedVolume, currentMonth } = props.progressData
  const expectedPerMonth = anticipatedVolume / 12

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  return months.map((name, index) => {
    const expected = expectedPerMonth * (index + 1)
    const actual = actualTurnover
    const percentage = expected > 0 ? Math.min((actual / expected) * 100, 100) : 0

    return {
      name,
      expected,
      actual,
      percentage: Math.round(percentage),
      isCurrentMonth: index === currentMonth,
      isPastMonth: index < currentMonth,
      isFutureMonth: index > currentMonth,
    }
  })
}

const showMonthPopover = (event: MouseEvent, monthIndex: number) => {
  mousePosition.value = {
    x: event.clientX,
    y: event.clientY,
  }

  selectedMonth.value = monthData.value[monthIndex]
  showPopover.value = true
}

const hideMonthPopover = () => {
  showPopover.value = false
  selectedMonth.value = null
}

watch(
  () => props.isDataLoaded,
  (isLoaded) => {
    if (isLoaded) {
      setTimeout(() => {
        draw()
      }, 100)
    }
  },
  { immediate: true },
)

onMounted(() => {
  if (props.isDataLoaded) {
    setTimeout(() => {
      draw()
    }, 100)
  }
})

const polarToCartesian = (
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number,
) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  }
}

const describeArc = (
  x: number,
  y: number,
  radius: number,
  startAngle: number,
  endAngle: number,
) => {
  const start = polarToCartesian(x, y, radius, startAngle),
    end = polarToCartesian(x, y, radius, endAngle),
    arcSweep = endAngle - startAngle <= 180 ? '0' : '1',
    d = ['M', start.x, start.y, 'A', radius, radius, 0, arcSweep, 1, end.x, end.y].join(' ')
  return d
}

const draw = () => {
  const len = (360 - 5 * totalSegments) / totalSegments
  let prevStartAngle = 0,
    prevEndAngle = 0,
    segment = ''

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]

  monthData.value = calculateMonthData()

  const calculateSegmentProgress = (monthIndex: number) => {
    const { actualTurnover, anticipatedVolume } = props.progressData

    if (anticipatedVolume === 0)
      return { progress: 0, isComplete: false, previousMonthComplete: false }

    const actualProgressUpToMonth = actualTurnover
    const expectedProgressPerMonth = anticipatedVolume / 12

    const monthExpected = expectedProgressPerMonth * (monthIndex + 1)
    const previousMonthExpected = monthIndex > 0 ? expectedProgressPerMonth * monthIndex : 0

    const previousMonthComplete =
      monthIndex > 0 ? actualProgressUpToMonth >= previousMonthExpected : true

    const monthActualProgress = Math.max(0, actualProgressUpToMonth - previousMonthExpected)
    const monthExpectedProgress = monthExpected - previousMonthExpected

    const progress =
      monthExpectedProgress > 0 ? Math.min(monthActualProgress / monthExpectedProgress, 1) : 0

    const isComplete = actualProgressUpToMonth >= monthExpected

    return {
      progress: progress,
      isComplete: isComplete,
      previousMonthComplete: previousMonthComplete,
    }
  }

  for (let i = 1; i <= totalSegments; i++) {
    prevStartAngle = prevEndAngle + 5
    prevEndAngle = len * i + 5 * i

    const segmentData = calculateSegmentProgress(i - 1)
    const segmentProgress = segmentData.progress
    let segmentClass = 'segment-path'
    const animationDelay = (i - 1) * 100
    const isPartial =
      segmentProgress > 0 && segmentProgress < 1 && segmentData.previousMonthComplete
    const arcPath = describeArc(250, 250, 200, prevStartAngle, prevEndAngle)

    if (segmentProgress === 1) {
      segmentClass += ' filled'
      segment += `<path
        class="${segmentClass}"
        d="${arcPath}"
        data-month-index="${i - 1}"
        data-progress="${segmentProgress}"
        data-animation-delay="${animationDelay}"
        style="opacity: 0;"
      />`
    } else if (isPartial) {
      const arcLength = ((prevEndAngle - prevStartAngle) / 360) * 2 * Math.PI * 200
      const dashLength = arcLength * segmentProgress
      const dashArray = `${dashLength} ${arcLength}`

      segment += `<path
        class="${segmentClass} segment-background"
        d="${arcPath}"
        data-month-index="${i - 1}"
      />
      <path
        class="${segmentClass} segment-fill"
        d="${arcPath}"
        data-month-index="${i - 1}"
        data-progress="${segmentProgress}"
        data-animation-delay="${animationDelay}"
        stroke-dasharray="${dashArray}"
        style="opacity: 0;"
      />`
    } else {
      segment += `<path
        class="${segmentClass}"
        d="${arcPath}"
        data-month-index="${i - 1}"
        data-progress="${segmentProgress}"
      />`
    }
  }

  let monthLabels = ''
  for (let i = 0; i < totalSegments; i++) {
    const angle = i * 30 + 15
    const labelRadius = 240
    const labelPos = polarToCartesian(250, 250, labelRadius, angle)

    monthLabels += `<text
      x="${labelPos.x}"
      y="${labelPos.y}"
      text-anchor="middle"
      dominant-baseline="middle"
      class="month-label"
      font-size="8"
      font-weight="700"
      fill="var(--p-surface-700)"
    >${months[i].toUpperCase()}</text>`
  }

  const currentDate = new Date()
  const currentMonth = currentDate.getMonth()
  const currentDay = currentDate.getDate()
  const daysInMonth = new Date(currentDate.getFullYear(), currentMonth + 1, 0).getDate()
  const monthProgress = currentDay / daysInMonth
  const currentAngle = currentMonth * 30 + monthProgress * 30

  const indicatorRadius = 200
  const indicatorPos = polarToCartesian(250, 250, indicatorRadius, currentAngle)

  const currentDateIndicator = `<circle
    cx="${indicatorPos.x}"
    cy="${indicatorPos.y}"
    r="5"
    class="current-date-indicator"
    fill="white"
    stroke="var(--p-primary-500)"
    stroke-width="2.5"
  />`

  if (svg.value) {
    svg.value.innerHTML = segment + monthLabels + currentDateIndicator

    requestAnimationFrame(() => {
      const animatedPaths = svg.value?.querySelectorAll(
        '.segment-path.filled, .segment-path.segment-fill',
      )
      animatedPaths?.forEach((path) => {
        const delay = parseInt(path.getAttribute('data-animation-delay') || '0')
        const element = path as SVGPathElement

        setTimeout(() => {
          element.style.transition = 'opacity 0.6s ease-out'
          element.style.opacity = '1'
        }, delay)
      })

      setTimeout(() => {
        const partialBackgrounds = svg.value?.querySelectorAll('.segment-background')
        partialBackgrounds?.forEach((path) => {
          const monthIndex = parseInt(path.getAttribute('data-month-index') || '0')
          const element = path as SVGPathElement
          element.style.pointerEvents = 'auto'
          element.style.cursor = 'pointer'
          path.addEventListener('mouseenter', (event) => {
            showMonthPopover(event as MouseEvent, monthIndex)
          })
          path.addEventListener('mousemove', (event) => {
            mousePosition.value = {
              x: (event as MouseEvent).clientX,
              y: (event as MouseEvent).clientY,
            }
          })
          path.addEventListener('mouseleave', hideMonthPopover)
        })

        const partialFills = svg.value?.querySelectorAll('.segment-path.segment-fill')
        partialFills?.forEach((path) => {
          const monthIndex = parseInt(path.getAttribute('data-month-index') || '0')
          path.addEventListener('mouseenter', (event) => {
            showMonthPopover(event as MouseEvent, monthIndex)
          })
          path.addEventListener('mousemove', (event) => {
            mousePosition.value = {
              x: (event as MouseEvent).clientX,
              y: (event as MouseEvent).clientY,
            }
          })
          path.addEventListener('mouseleave', hideMonthPopover)
        })

        const segmentPaths = svg.value?.querySelectorAll(
          '.segment-path:not(.segment-background):not(.segment-fill)',
        )
        segmentPaths?.forEach((path) => {
          const monthIndex = parseInt(path.getAttribute('data-month-index') || '0')
          path.addEventListener('mouseenter', (event) => {
            showMonthPopover(event as MouseEvent, monthIndex)
          })
          path.addEventListener('mousemove', (event) => {
            mousePosition.value = {
              x: (event as MouseEvent).clientX,
              y: (event as MouseEvent).clientY,
            }
          })
          path.addEventListener('mouseleave', hideMonthPopover)
        })
      }, 10)
    })
  }
}
</script>

<style>
#svg-container {
  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 1.5rem;
    padding: 2px;
    background: linear-gradient(
      135deg,
      var(--p-primary-200),
      var(--p-primary-100),
      var(--p-primary-200)
    );
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    mask-composite: exclude;
    opacity: 0.5;
    pointer-events: none;
  }
}

#segCirc {
  path,
  circle {
    fill: none;
    stroke-width: 32;
    stroke: var(--p-primary-100);
    transition: all 0.3s ease;

    &.filled {
      stroke: var(--p-primary-500);
    }

    &.current-date-indicator {
      fill: white;
      stroke: white;
    }
  }

  .segment-path {
    fill: var(--p-primary-50);
    stroke: var(--p-primary-100);
    stroke-width: 32;
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover {
      stroke-width: 38;
      filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2));
      transform: scale(1.02);
    }

    &.filled {
      fill: var(--p-primary-100);
      stroke: var(--p-primary-500);
    }

    &.partial {
      fill: var(--p-primary-50);
      stroke: var(--p-primary-500);
      stroke-dashoffset: 0;
    }

    &.segment-background {
      fill: var(--p-primary-50);
      stroke: var(--p-primary-100);
      stroke-width: 32;
      pointer-events: none;
    }

    &.segment-fill {
      fill: none;
      stroke: var(--p-primary-500);
      stroke-width: 32;
      pointer-events: auto;
    }
  }

  @keyframes segmentFillIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @keyframes segmentColorFill {
    0% {
      fill: var(--p-primary-100);
      stroke: var(--p-primary-500);
    }
    50% {
      fill: var(--p-orange-100);
      stroke: var(--p-orange-500);
    }
    100% {
      fill: var(--p-mint-100);
      stroke: var(--p-mint-500);
    }
  }

  @keyframes segmentColorPartial {
    0% {
      fill: var(--p-mint-100);
      stroke: var(--p-mint-500);
    }
    100% {
      fill: var(--p-orange-100);
      stroke: var(--p-orange-500);
    }
  }

  .month-label {
    font-family: var(--font-family);
    font-size: 11px;
    font-weight: 700;
    fill: var(--p-surface-700);
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    pointer-events: none;
    user-select: none;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .current-date-indicator {
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
    animation: pulse 2s infinite;
    stroke-width: 12;
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.7;
      transform: scale(1.1);
    }
  }

  .dark-selector .month-label {
    fill: var(--p-surface-400);
  }

  .dark-selector .current-date-indicator {
    fill: white;
    stroke: var(--p-primary-400);
  }

  .a11y-high-contrast .month-label {
    fill: var(--p-surface-900);
    font-weight: 700;
  }

  .a11y-high-contrast .current-date-indicator {
    fill: white;
    stroke: var(--p-primary-600);
    stroke-width: 3;
  }

  .a11y-reduced-motion .current-date-indicator {
    animation: none;
  }

  .a11y-reduced-motion .segment-path {
    animation: none;
    opacity: 1;
  }
}

.popover-fade-enter-active,
.popover-fade-leave-active {
  transition: all 0.2s ease-out;
}

.popover-fade-enter-from {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

.popover-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}

.dark-selector .month-progress-popover {
  .popover-arrow {
    border-bottom-color: var(--p-surface-800);
  }

  .popover-content {
    background: var(--p-surface-800);
    border: 1px solid var(--p-surface-700);
  }

  .popover-header {
    background: linear-gradient(135deg, var(--p-primary-900) 0%, var(--p-primary-800) 100%);
    border-bottom-color: var(--p-surface-700);
  }

  .popover-title {
    color: var(--p-anthracite-500);
  }

  .popover-subtitle {
    color: var(--p-anthracite-500);
  }

  .popover-body {
    background: var(--p-surface-800);
  }

  .stat-item {
    background: var(--p-surface-700);

    &.highlight {
      background: linear-gradient(135deg, var(--p-primary-900) 0%, var(--p-primary-800) 100%);
      border-color: var(--p-primary-700);
    }
  }

  .stat-label {
    color: var(--p-anthracite-500);
  }

  .stat-value {
    color: var(--p-anthracite-500);

    &.expected-value {
      color: var(--p-primary-300);
    }

    &.actual-value {
      color: var(--p-mint-300);
    }

    &.progress-percentage {
      color: var(--p-tangerine-300);
    }
  }

  .progress-bar-labels {
    color: var(--p-anthracite-500);
  }
}

.segment-legend {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--p-surface-200);
  min-width: 200px;
  z-index: 10;
}

.legend-title {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--p-anthracite-500);
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--p-surface-200);
}

.legend-items {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 2px solid;
  flex-shrink: 0;
}

.legend-color.unfilled {
  background: var(--p-primary-100);
  border-color: var(--p-primary-200);
}

.legend-color.partial {
  background: var(--p-orange-100);
  border-color: var(--p-orange-500);
}

.legend-color.filled {
  background: var(--p-mint-100);
  border-color: var(--p-mint-500);
}

.legend-label {
  font-size: 0.8125rem;
  color: var(--p-anthracite-500);
  font-weight: 500;
}

.dark-selector .segment-legend {
  background: rgba(30, 30, 30, 0.95);
  border-color: var(--p-surface-700);
}

.dark-selector .legend-title {
  color: var(--p-anthracite-500);
  border-bottom-color: var(--p-surface-700);
}

.dark-selector .legend-label {
  color: var(--p-anthracite-500);
}
.a11y-high-contrast .segment-legend {
  border: 2px solid var(--p-primary-500);
  background: var(--p-surface-0);
}

.a11y-high-contrast .legend-color {
  border-width: 3px;
}

@media (max-width: 768px) {
  .segment-legend {
    position: relative;
    bottom: auto;
    left: auto;
    margin-top: 1rem;
    min-width: auto;
    width: 100%;
  }
}
</style>
